/**
 * Encode a MedCom document for download so the file bytes match the XML
 * declaration. Stored receipts (`standard_exchanges.payload`) are UTF-8 text,
 * but the header often says `encoding="ISO-8859-1"`. A string handed to Blob
 * is written as UTF-8, so "understøttes" would become two wrong characters
 * when Testcenter reads the declaration.
 *
 * The payload text and the declaration are left unchanged. Only the on-disk
 * bytes are recoded.
 *
 * A character above U+00FF has no Latin-1 byte. Rather than substitute one and
 * corrupt a certification file, it is written as a numeric character reference.
 */
function looksLikeEdifact(text: string): boolean {
  const head = text.trimStart();
  return head.startsWith('UNA') || head.startsWith('UNB');
}

function encodeLatin1(text: string): BlobPart {
  const bytes: number[] = [];
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    if (code <= 0xff) {
      bytes.push(code);
      continue;
    }
    for (const digit of `&#${code};`) bytes.push(digit.charCodeAt(0));
  }
  return Uint8Array.from(bytes);
}

export function encodeMedcomDocument(text: string): { body: BlobPart; type: string } {
  if (looksLikeEdifact(text)) {
    return { body: encodeLatin1(text), type: 'application/edifact; charset=iso-8859-1' };
  }
  const declared = /encoding=["']([^"']+)["']/i.exec(text.slice(0, 200))?.[1]?.toLowerCase();
  if (declared === undefined || declared.startsWith('utf')) {
    return { body: text, type: 'application/xml; charset=utf-8' };
  }
  return { body: encodeLatin1(text), type: `application/xml; charset=${declared}` };
}

export function downloadMedcomDocument(name: string, contents: string) {
  const { body, type } = encodeMedcomDocument(contents);
  const blob = new Blob([body], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}
